import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { DropdownItem } from '@vueling-ng/vy-ds/src/lib/components/globals/forms/dropdown';
import { InputAttrTypeEnum } from '@vueling-ng/vy-ds/src/lib/components/globals/forms/input';
import { SegmentedControlsModel } from '@vueling-ng/vy-ds/src/lib/components/globals/segmented-controls';
import { ChannelType, Filters } from './bookings.interface';

export type FilterControl =
  | 'recordLocator'
  | 'firstName'
  | 'lastName'
  | 'departureCode'
  | 'returnCode'
  | 'departureDate'
  | 'returnDate'
  | 'channelType'
  | 'status';

export interface FilterTextBox {
  filterName: FilterControl;
  filterType: InputAttrTypeEnum;
  label: string;
  validator?: ValidationErrors | ValidatorFn[];
  options?: DropdownItem[];
}

export const isValidDate = (control: AbstractControl): ValidationErrors | null => {
  const value = control.value as string;
  if (value) {
    const [day, month, year] = value.split('/');
    const isoString = `${year}-${month}-${day}`;

    if (!day || !month || !year || isNaN(Date.parse(isoString))) {
      return { invalidDate: true };
    }
  }

  return null;
};

export const emptyFilters: Filters = {
  status: undefined,
  departureCode: undefined,
  returnCode: undefined,
  departureDate: undefined,
  returnDate: undefined,
  channelType: undefined,
  recordLocator: undefined,
  firstName: undefined,
  lastName: undefined
};

export type QuickFilters =
  | 'None'
  | 'PendingQuickFilters'
  | 'FlownQuickFilters'
  | 'CancelledQuickFilters'
  | 'Available'
  | 'Used';

export const QuickFiltersValue: Record<QuickFilters, QuickFilters> = {
  None: 'None',
  PendingQuickFilters: 'PendingQuickFilters',
  FlownQuickFilters: 'FlownQuickFilters',
  CancelledQuickFilters: 'CancelledQuickFilters',
  Available: 'Available',
  Used: 'Used'
};

export type DefaultOptions = 'Default' | 'DefaultChannelType';

export const DefaultOptionValue: Record<DefaultOptions, string> = {
  Default: 'None',
  DefaultChannelType: ChannelType.B2B
};

export const defaultOption: DropdownItem = {
  label: 'booking_list_component.filters.default_option',
  value: DefaultOptionValue.Default
};

export const quickFiltersCredit: SegmentedControlsModel[] = [
  {
    label: 'booking_list_component.status.all',
    value: QuickFiltersValue.None,
    checked: true
  },
  {
    label: 'booking_list_component.status.available',
    value: QuickFiltersValue.Available,
    checked: false
  },
  {
    label: 'booking_list_component.status.used',
    value: QuickFiltersValue.Used,
    checked: false
  }
];

export const quickFilters: SegmentedControlsModel[] = [
  {
    label: 'booking_list_component.status.all',
    value: QuickFiltersValue.None,
    checked: true
  },
  {
    label: 'booking_list_component.status.pending',
    value: QuickFiltersValue.PendingQuickFilters,
    checked: false
  },
  {
    label: 'booking_list_component.status.flown',
    value: QuickFiltersValue.FlownQuickFilters,
    checked: false
  },
  {
    label: 'booking_list_component.status.cancelled',
    value: QuickFiltersValue.CancelledQuickFilters,
    checked: false
  }
];

const channelTypeOptions: DropdownItem[] = [
  {
    label: 'booking_list_component.filters.default_option',
    value: DefaultOptionValue.DefaultChannelType
  },
  {
    label: ChannelType.GDS,
    value: ChannelType.GDS
  },
  {
    label: ChannelType.TTOO,
    value: ChannelType.TTOO
  }
];

export const DefaultFilters: FilterTextBox[] = [
  {
    filterName: 'recordLocator',
    filterType: InputAttrTypeEnum.TEXT,
    label: 'booking_list_component.bookings_table_headers.record_locator'
  },
  {
    filterName: 'status',
    filterType: InputAttrTypeEnum.TEXT,
    label: 'booking_list_component.filters.status',
    options: [
      defaultOption,
      {
        label: 'booking_list_component.status_badges.pending_payment',
        value: 'PendingPayment'
      },
      {
        label: 'booking_list_component.status_badges.pending_process',
        value: 'PendingProcess'
      },
      {
        label: 'booking_list_component.status_badges.pending_refund',
        value: 'PendingRefund'
      },
      {
        label: 'booking_list_component.status_badges.pending_flight',
        value: 'PendingFlight'
      },
      {
        label: 'booking_list_component.status_badges.flown',
        value: 'Flown'
      },
      {
        label: 'booking_list_component.status.cancelled',
        value: 'Cancelled'
      },
      {
        label: 'booking_list_component.status.refunded',
        value: 'Refunded'
      }
    ]
  },
  {
    filterName: 'firstName',
    filterType: InputAttrTypeEnum.TEXT,
    label: 'booking_list_component.filters.pax_name'
  },
  {
    filterName: 'lastName',
    filterType: InputAttrTypeEnum.TEXT,
    label: 'booking_list_component.filters.pax_surname'
  },
  {
    filterName: 'departureCode',
    filterType: InputAttrTypeEnum.TEXT,
    label: 'booking_list_component.filters.origin'
  },
  {
    filterName: 'returnCode',
    filterType: InputAttrTypeEnum.TEXT,
    label: 'booking_list_component.filters.destination'
  },
  {
    filterName: 'departureDate',
    filterType: InputAttrTypeEnum.DATE,
    label: 'booking_list_component.bookings_table_headers.outbound_date',
    validator: isValidDate
  },
  {
    filterName: 'returnDate',
    filterType: InputAttrTypeEnum.DATE,
    label: 'booking_list_component.bookings_table_headers.inbound_date',
    validator: isValidDate
  },
  {
    filterName: 'channelType',
    filterType: InputAttrTypeEnum.TEXT,
    label: 'booking_list_component.filters.channel',
    options: channelTypeOptions
  }
];

export const PendingFilters: FilterTextBox[] = [
  {
    filterName: 'recordLocator',
    filterType: InputAttrTypeEnum.TEXT,
    label: 'booking_list_component.bookings_table_headers.record_locator'
  },
  {
    filterName: 'status',
    filterType: InputAttrTypeEnum.TEXT,
    label: 'booking_list_component.filters.status',
    options: [
      defaultOption,
      {
        label: 'booking_list_component.status_badges.pending_payment',
        value: 'PendingPayment'
      },
      {
        label: 'booking_list_component.status_badges.pending_process',
        value: 'PendingProcess'
      },
      {
        label: 'booking_list_component.status_badges.pending_flight',
        value: 'PendingFlight'
      }
    ]
  },
  {
    filterName: 'firstName',
    filterType: InputAttrTypeEnum.TEXT,
    label: 'booking_list_component.filters.pax_name'
  },
  {
    filterName: 'lastName',
    filterType: InputAttrTypeEnum.TEXT,
    label: 'booking_list_component.filters.pax_surname'
  },
  {
    filterName: 'departureCode',
    filterType: InputAttrTypeEnum.TEXT,
    label: 'booking_list_component.filters.origin'
  },
  {
    filterName: 'returnCode',
    filterType: InputAttrTypeEnum.TEXT,
    label: 'booking_list_component.filters.destination'
  },
  {
    filterName: 'departureDate',
    filterType: InputAttrTypeEnum.DATE,
    label: 'booking_list_component.bookings_table_headers.outbound_date',
    validator: isValidDate
  },
  {
    filterName: 'returnDate',
    filterType: InputAttrTypeEnum.DATE,
    label: 'booking_list_component.bookings_table_headers.inbound_date',
    validator: isValidDate
  },
  {
    filterName: 'channelType',
    filterType: InputAttrTypeEnum.TEXT,
    label: 'booking_list_component.filters.channel',
    options: channelTypeOptions
  }
];

export const FlownFilters: FilterTextBox[] = [
  {
    filterName: 'recordLocator',
    filterType: InputAttrTypeEnum.TEXT,
    label: 'booking_list_component.bookings_table_headers.record_locator'
  },
  {
    filterName: 'firstName',
    filterType: InputAttrTypeEnum.TEXT,
    label: 'booking_list_component.filters.pax_name'
  },
  {
    filterName: 'lastName',
    filterType: InputAttrTypeEnum.TEXT,
    label: 'booking_list_component.filters.pax_surname'
  },
  {
    filterName: 'departureCode',
    filterType: InputAttrTypeEnum.TEXT,
    label: 'booking_list_component.filters.origin'
  },
  {
    filterName: 'returnCode',
    filterType: InputAttrTypeEnum.TEXT,
    label: 'booking_list_component.filters.destination'
  },
  {
    filterName: 'departureDate',
    filterType: InputAttrTypeEnum.DATE,
    label: 'booking_list_component.bookings_table_headers.outbound_date',
    validator: isValidDate
  },
  {
    filterName: 'returnDate',
    filterType: InputAttrTypeEnum.DATE,
    label: 'booking_list_component.bookings_table_headers.inbound_date',
    validator: isValidDate
  },
  {
    filterName: 'channelType',
    filterType: InputAttrTypeEnum.TEXT,
    label: 'booking_list_component.filters.channel',
    options: channelTypeOptions
  }
];

export const CancelledFilters: FilterTextBox[] = [
  {
    filterName: 'recordLocator',
    filterType: InputAttrTypeEnum.TEXT,
    label: 'booking_list_component.bookings_table_headers.record_locator'
  },
  {
    filterName: 'status',
    filterType: InputAttrTypeEnum.TEXT,
    label: 'booking_list_component.filters.status',
    options: [
      defaultOption,
      {
        label: 'booking_list_component.status.cancelled',
        value: 'Cancelled'
      },
      {
        label: 'booking_list_component.status.refunded',
        value: 'Refunded'
      }
    ]
  },
  {
    filterName: 'channelType',
    filterType: InputAttrTypeEnum.TEXT,
    label: 'booking_list_component.filters.channel',
    options: channelTypeOptions
  }
];

export const selectFilters: FilterControl[] = ['status', 'channelType', 'departureCode', 'returnCode'];
